// What the agent is doing, for the faces that show it (ECONIC, 23 Oct 2026: agents with faces that listen, think, work, answer and nod when
// done). Read from the live turn the panel already holds — never a second stream — and said in one word the host's face draws: listening
// while the person dictates or types, thinking until something happens, working while a tool or another agent runs (with that agent's
// key, so the Librarian's face works while it searches), speaking while the answer's text arrives, asking when the turn ends on a question,
// error when it failed, and "done" for a moment after an answer lands. The words are the same as the face's states (packages/ui faces).
import { useEffect, useRef, useState } from "react";
import type { LiveTurn } from "./hooks";

export type PresenceState = "idle" | "listening" | "thinking" | "working" | "speaking" | "done" | "asking" | "error";
export interface Presence {
  state: PresenceState;
  /** The agent at work when it is not the one the conversation is with (a delegation, a specialist's tool), else null. */
  agentKey: string | null;
}

export const IDLE: Presence = { state: "idle", agentKey: null };

/** Stages of the `status` frames that mean the agent is doing something rather than thinking (types.ts AssistantStreamEvent). */
const WORKING_STAGES = new Set(["retrieving", "tool", "delegating", "plan", "verifying", "surface", "thread"]);

export function presenceOf({ busy, streaming, live, listening = false, asking = false }: {
  busy: boolean;
  streaming: boolean;
  live: LiveTurn | null | undefined;
  /** The person is dictating or typing to the agent. */
  listening?: boolean;
  /** The last answer ended on a question the person has not answered. */
  asking?: boolean;
}): Presence {
  if (live?.error) return { state: "error", agentKey: null };
  if (streaming && live) {
    const running = [...live.toolCalls].reverse().find((c) => c.phase === "start");
    const delegated = live.delegations.length ? live.delegations[live.delegations.length - 1]!.agentKey : null;
    if (live.content.trim()) return { state: "speaking", agentKey: null };
    if (running) return { state: "working", agentKey: running.agentKey ?? delegated };
    if (live.stage && WORKING_STAGES.has(live.stage)) return { state: "working", agentKey: delegated };
    return { state: "thinking", agentKey: null };
  }
  if (live?.ask || asking) return { state: "asking", agentKey: null };
  if (listening) return { state: "listening", agentKey: null };
  if (busy || streaming) return { state: "thinking", agentKey: null };
  return IDLE;
}

/** presenceOf plus a moment of "done" (1.6 s) when a streamed answer lands without an error or a question: one timer per conversation. */
export function useAgentPresence(input: Parameters<typeof presenceOf>[0]): Presence {
  const now = presenceOf(input);
  const [done, setDone] = useState(false);
  const { streaming, live } = input;
  const was = useRef(streaming);
  const failed = !!live?.error || !!live?.ask;
  useEffect(() => {
    const landed = was.current && !streaming && !failed;
    was.current = streaming;
    if (!landed) return;
    setDone(true);
    const t = window.setTimeout(() => setDone(false), 1600);
    return () => { window.clearTimeout(t); setDone(false); };
    // Only the end of a stream starts the moment; `failed` is read as it is then.
  }, [streaming]);
  if (done && now.state === "idle") return { state: "done", agentKey: null };
  return now;
}
